import React, { useState, useEffect } from 'react';
import {
  X,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  FileCheck,
  Users,
  Package,
  ListChecks,
  RefreshCw,
  Play,
  ChevronDown,
  ChevronUp,
  HardDrive,
  FileText,
  Image,
  MessageSquare,
  Building2,
  Calendar,
  MapPin,
  Target,
  Zap,
  Briefcase,
  User,
  Hash,
  History,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import RightDrawer from '../../../shared/components/form/RightDrawer';
import CheckpointDetailCard from './CheckpointDetailCard';
import {
  AmsAuditApproval,
  getAuditApprovalById,
  submitAuditForReview,
  resubmitAudit,
  approveSection,
  rejectSection,
  approveCheckpoint,
  rejectCheckpoint,
  bulkApproveCheckpoints,
  bulkRejectCheckpoints,
  startAudit,
} from '../../../services/amsAuditApprovalService';
import amsProjectAuditService, { AmsProjectAudit } from '../../../services/amsProjectAuditService';
import amsProjectAssetService from '../../../services/amsProjectAssetService';
import { amsEquipmentService } from '../../../services/amsEquipmentService';
import amsAuditCheckpointService, { AmsAuditCheckpoint } from '../../../services/amsAuditCheckpointService';
import { useAuth } from '../../../context/AuthContext';
import { isApprover, isRequester } from '../../../utils/roleUtils';
import { Dialog } from 'primereact/dialog';
import RejectModal from './RejectModal';
import amsProjectService from '../../../services/amsProjectService';
import { resolveInitialAuditId } from '../utils/resolveInitialAuditId';
import AmsAuditHistoryDrawer from '../../amsProject/components/AmsAuditHistoryDrawer';

// audit.auditTypeId is a populated { _id, name } object from the backend, or
// occasionally a bare id string.
const getAuditTypeLabel = (audit: AmsProjectAudit | null | undefined): string => {
  if (!audit) return '';
  return typeof audit.auditTypeId === 'string' ? audit.auditTypeId : audit.auditTypeId?.name || '';
};

interface AmsAuditApprovalDrawerProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  onApprovalComplete?: () => void;
  initialTab?: 'projectInfo' | 'audits';
  initialAuditId?: string | null;
}

const AmsAuditApprovalDrawer: React.FC<AmsAuditApprovalDrawerProps> = ({
  open,
  onClose,
  projectId,
  onApprovalComplete,
  initialTab,
  initialAuditId,
}) => {
  const { user } = useAuth();
  const [project, setProject] = useState<any>(null);
  const [activeAudits, setActiveAudits] = useState<AmsProjectAudit[]>([]);
  const [auditApprovals, setAuditApprovals] = useState<Map<string, AmsAuditApproval>>(new Map());
  const [selectedAuditId, setSelectedAuditId] = useState<string | null>(null);
  const [selectedApproval, setSelectedApproval] = useState<AmsAuditApproval | null>(null);
  const [selectedAudit, setSelectedAudit] = useState<AmsProjectAudit | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectSectionName, setRejectSectionName] = useState<'auditType' | 'auditors' | 'assetsAndEquipment' | null>(null);
  const [rejectCheckpointId, setRejectCheckpointId] = useState<string | null>(null);
  const [rejectComments, setRejectComments] = useState('');
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitComments, setSubmitComments] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  
  // Additional data for detailed view
  const [projectAssets, setProjectAssets] = useState<any[]>([]);
  const [projectEquipment, setProjectEquipment] = useState<any[]>([]);
  const [detailedCheckpoints, setDetailedCheckpoints] = useState<{
    project: AmsAuditCheckpoint[];
    asset: AmsAuditCheckpoint[];
  }>({ project: [], asset: [] });
  const [expandedAssets, setExpandedAssets] = useState<Set<string>>(new Set());
  const [selectedCheckpoints, setSelectedCheckpoints] = useState<Set<string>>(new Set());
  const [rejectActionType, setRejectActionType] = useState<'section' | 'checkpoint' | 'bulk-checkpoints' | null>(null);
  const [bulkRejectCheckpointIds, setBulkRejectCheckpointIds] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'projectInfo' | 'audits'>(initialTab ?? 'projectInfo');
  
  // Project-level approval sections state
  const [projectApprovalSections, setProjectApprovalSections] = useState<{
    projectOverview?: {
      status: 'not_required' | 'pending' | 'approved' | 'rejected';
      approverId?: any;
      approverComments?: string;
      approvedAt?: Date;
      rejectedAt?: Date;
    };
    assetsAndEquipment?: {
      status: 'not_required' | 'pending' | 'approved' | 'rejected';
      approverId?: any;
      approverComments?: string;
      approvedAt?: Date;
      rejectedAt?: Date;
    };
  } | null>(null);

  const userIsApprover = isApprover('AMS');
  const userIsRequester = isRequester('AMS');

  // For AMS requester: highlight sections by approval status when drawer is open
  const getSectionHighlightForRequester = (status: string | undefined) => {
    if (!userIsRequester || !status) return '';
    if (status === 'approved') return 'ring-2 ring-green-400/60 bg-green-50/50 border-green-200';
    if (status === 'rejected') return 'ring-2 ring-red-400/60 bg-red-50/40 border-red-200';
    return '';
  };

  // For AMS requester: audit overall status highlight (e.g. rejected / approved_to_start)
  const getAuditPillHighlightForRequester = (overallStatus: string | undefined) => {
    if (!userIsRequester || !overallStatus) return '';
    if (overallStatus === 'rejected') return 'ring-2 ring-red-400/60 bg-red-50/70 border-red-300';
    if (overallStatus === 'approved_to_start' || overallStatus === 'partially_approved') return 'ring-2 ring-green-400/50 bg-green-50/60 border-green-300';
    return '';
  };

  // Tab configuration
  const tabs = [
    { id: 'projectInfo' as const, label: 'Project Info', icon: Building2 },
    { id: 'audits' as const, label: 'Audits', icon: FileCheck },
  ];

  useEffect(() => {
    if (open && projectId) {
      loadProjectData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId, initialAuditId]);

  // Update selected approval and audit when selectedAuditId changes
  useEffect(() => {
    if (selectedAuditId) {
      const audit = activeAudits.find(a => String(a._id || (a as any).id) === selectedAuditId);
      const approval = auditApprovals.get(selectedAuditId);
      setSelectedAudit(audit || null);
      setSelectedApproval(approval || null);
      if (audit) {
        loadDetailedCheckpoints(selectedAuditId);
      }
    }
  }, [selectedAuditId, activeAudits, auditApprovals]);

  /** Refresh only one audit's approval (for section/checkpoint approve-reject). Does not reload project or change tab/selection. */
  const refreshAuditApproval = async (auditId: string) => {
    try {
      const approval = await getAuditApprovalById(auditId);
      setAuditApprovals(prev => new Map(prev).set(auditId, approval));
    } catch (error: any) {
      console.warn('Failed to refresh audit approval:', error);
      toast.error(error.response?.data?.message || 'Failed to refresh approval');
    }
  };

  const loadProjectData = async (preserve?: { preserveAuditId?: string | null }) => {
    try {
      setLoading(true);
      const projectData = await amsProjectService.getProjectById(projectId);
      setProject(projectData);
      
      if (projectData.projectApprovalSections) {
        setProjectApprovalSections(projectData.projectApprovalSections);
      } else {
        if (projectData.projectLevelApprovalStatus || projectData.assetsAndEquipmentApprovalStatus) {
          setProjectApprovalSections({
            projectOverview: projectData.projectLevelApprovalStatus ? {
              status: projectData.projectLevelApprovalStatus,
              approverId: projectData.projectLevelApproverId,
              approverComments: projectData.projectLevelApproverComments,
              approvedAt: projectData.projectLevelApprovedAt,
              rejectedAt: projectData.projectLevelRejectedAt,
            } : undefined,
            assetsAndEquipment: projectData.assetsAndEquipmentApprovalStatus ? {
              status: projectData.assetsAndEquipmentApprovalStatus,
              approverId: projectData.assetsAndEquipmentApproverId,
              approverComments: projectData.assetsAndEquipmentApproverComments,
              approvedAt: projectData.assetsAndEquipmentApprovedAt,
              rejectedAt: projectData.assetsAndEquipmentRejectedAt,
            } : undefined,
          });
        } else {
          setProjectApprovalSections(null);
        }
      }

      const allAudits = await amsProjectAuditService.getProjectAudits(projectId);
      const allActive = allAudits.filter(a => a.status === 'active');

      const approvalMap = new Map<string, AmsAuditApproval>();
      for (const audit of allActive) {
        const auditId = String(audit._id || (audit as any).id);
        try {
          const approval = await getAuditApprovalById(auditId);
          approvalMap.set(auditId, approval);
        } catch (error: any) {
          console.warn(`No approval record found for audit ${auditId}:`, error);
        }
      }
      setAuditApprovals(approvalMap);

      // Approvers review only SUBMITTED audits — hide never-submitted drafts (the
      // approvals list hides them too). Requesters still see drafts so they can submit them.
      const active = userIsApprover
        ? allActive.filter(a => approvalMap.get(String(a._id || (a as any).id))?.submittedForReview)
        : allActive;
      setActiveAudits(active);

      if (active.length > 0) {
        // Deterministic selection (no mutable "applied" ref — it raced under
        // StrictMode's double-invoke and stuck the tab on the first audit):
        // preserve target wins, else the clicked audit, else the first.
        const nextSelectedId = resolveInitialAuditId(
          active,
          preserve?.preserveAuditId,
          initialAuditId,
          false
        );
        if (nextSelectedId) setSelectedAuditId(nextSelectedId);
      }

      await Promise.all([
        loadProjectAssets(projectId),
        loadProjectEquipment(projectId),
      ]);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load project data');
    } finally {
      setLoading(false);
    }
  };

  const loadProjectAssets = async (projectId: string) => {
    try {
      const assets = await amsProjectAssetService.getAmsProjectAssetsByProject(projectId);
      setProjectAssets(assets);
    } catch (error: any) {
      console.error('Failed to load project assets:', error);
    }
  };

  const loadProjectEquipment = async (projectId: string) => {
    try {
      const response = await amsEquipmentService.getEquipment({ projectId });
      const equipment = Array.isArray(response.data) ? response.data : [];
      // Filter for active equipment
      const activeEquipment = equipment.filter((eq: any) => eq.status === 'active' || !eq.status);
      setProjectEquipment(activeEquipment);
    } catch (error: any) {
      console.error('Failed to load project equipment:', error);
    }
  };

  const loadDetailedCheckpoints = async (auditId: string) => {
    try {
      const [projectCheckpoints, assetCheckpoints] = await Promise.all([
        amsAuditCheckpointService.getAuditCheckpoints(auditId, 'project'),
        amsAuditCheckpointService.getAuditCheckpoints(auditId, 'asset'),
      ]);
      setDetailedCheckpoints({
        project: projectCheckpoints,
        asset: assetCheckpoints,
      });
    } catch (error: any) {
      console.error('Failed to load detailed checkpoints:', error);
    }
  };

  const toggleAssetExpand = (assetId: string) => {
    setExpandedAssets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(assetId)) {
        newSet.delete(assetId);
      } else {
        newSet.add(assetId);
      }
      return newSet;
    });
  };


  const getCheckpointDetails = (checkpointId: string, type: 'project' | 'asset'): AmsAuditCheckpoint | null => {
    const checkpoints = type === 'project' ? detailedCheckpoints.project : detailedCheckpoints.asset;
    
    // Normalize checkpointId to string
    const normalizedId = String(checkpointId);
    
    // Try to find by _id first, then by id field
    return checkpoints.find(cp => {
      const cpId = cp._id || (cp as any).id;
      return String(cpId) === normalizedId;
    }) || null;
  };

  const getEquipmentByAsset = (assetId: string) => {
    return projectEquipment.filter(eq => {
      const eqAssetId = typeof eq.projectAssetId === 'object' && eq.projectAssetId
        ? (eq.projectAssetId._id || (eq.projectAssetId as any).id)
        : eq.projectAssetId;
      return eqAssetId === assetId;
    });
  };

  const handleApproveSection = async (sectionName: 'auditType' | 'auditors' | 'assetsAndEquipment') => {
    if (!selectedAuditId) return;
    try {
      setActionLoading(true);
      await approveSection(selectedAuditId, sectionName);
      toast.success(`${sectionName} approved successfully`);
      await refreshAuditApproval(selectedAuditId);
      onApprovalComplete?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to approve section');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectSection = async () => {
    if (!selectedAuditId || !rejectSectionName || !rejectComments.trim()) {
      toast.error('Rejection comments are required');
      return;
    }

    try {
      setActionLoading(true);
      await rejectSection(selectedAuditId, rejectSectionName, rejectComments);
      toast.success(`${rejectSectionName} rejected successfully`);
      setShowRejectModal(false);
      setRejectSectionName(null);
      setRejectComments('');
      setRejectActionType(null);
      await refreshAuditApproval(selectedAuditId);
      onApprovalComplete?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to reject section');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveCheckpoint = async (checkpointId: string) => {
    if (!selectedAuditId) return;
    try {
      setActionLoading(true);
      await approveCheckpoint(selectedAuditId, checkpointId);
      toast.success('Checkpoint approved successfully');
      await refreshAuditApproval(selectedAuditId);
      onApprovalComplete?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to approve checkpoint');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectCheckpoint = async () => {
    if (!selectedAuditId || !rejectComments.trim()) {
      toast.error('Rejection comments are required');
      return;
    }

    try {
      setActionLoading(true);
      
      // Handle bulk rejection
      if (rejectActionType === 'bulk-checkpoints' && bulkRejectCheckpointIds.length > 0) {
        await bulkRejectCheckpoints(selectedAuditId, bulkRejectCheckpointIds, rejectComments);
        toast.success(`${bulkRejectCheckpointIds.length} checkpoint(s) rejected successfully`);
        setBulkRejectCheckpointIds([]);
      } else if (rejectCheckpointId) {
        // Single checkpoint rejection
        await rejectCheckpoint(selectedAuditId, rejectCheckpointId, rejectComments);
        toast.success('Checkpoint rejected successfully');
      } else {
        toast.error('No checkpoint selected for rejection');
        return;
      }
      
      setShowRejectModal(false);
      setRejectCheckpointId(null);
      setRejectComments('');
      setRejectActionType(null);
      setSelectedCheckpoints(new Set());
      if (selectedAuditId) await refreshAuditApproval(selectedAuditId);
      onApprovalComplete?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to reject checkpoint');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleCheckpointSelection = (checkpointId: string) => {
    setSelectedCheckpoints(prev => {
      const newSet = new Set(prev);
      if (newSet.has(checkpointId)) {
        newSet.delete(checkpointId);
      } else {
        newSet.add(checkpointId);
      }
      return newSet;
    });
  };

  // AMS checkpointId refs expose `id` (virtual), not `_id` — matches the
  // extraction already used when rendering CheckpointDetailCard below. The
  // previous version of this helper fell through to `String(cp.checkpointId)`
  // (the whole object) whenever `_id` was missing, so every checkpoint
  // collapsed to the literal string "[object Object]" and a Set of them
  // silently deduped to one entry.
  const extractCheckpointId = (cp: AmsAuditApproval['checkpointApprovals']['projectCheckpoints'][number]): string => {
    if (typeof cp.checkpointId === 'object' && cp.checkpointId) {
      return cp.checkpointId._id || (cp.checkpointId as any).id || '';
    }
    return String(cp.checkpointId || '');
  };

  const selectAllProjectCheckpoints = () => {
    if (!selectedApproval) return;
    const pendingCheckpoints = selectedApproval.checkpointApprovals.projectCheckpoints
      .filter(cp => cp.status === 'pending')
      .map(extractCheckpointId)
      .filter(Boolean);

    if (pendingCheckpoints.length === 0) {
      toast.info('No pending project checkpoints to select');
      return;
    }

    setSelectedCheckpoints(prev => {
      const newSet = new Set(prev);
      pendingCheckpoints.forEach(id => newSet.add(id));
      return newSet;
    });
    toast.success(`Selected ${pendingCheckpoints.length} pending project checkpoint(s)`);
  };

  const selectAllAssetCheckpoints = () => {
    if (!selectedApproval) return;
    const pendingCheckpoints = selectedApproval.checkpointApprovals.assetCheckpoints
      .filter(cp => cp.status === 'pending')
      .map(extractCheckpointId)
      .filter(Boolean);

    if (pendingCheckpoints.length === 0) {
      toast.info('No pending asset checkpoints to select');
      return;
    }

    setSelectedCheckpoints(prev => {
      const newSet = new Set(prev);
      pendingCheckpoints.forEach(id => newSet.add(id));
      return newSet;
    });
    toast.success(`Selected ${pendingCheckpoints.length} pending asset checkpoint(s)`);
  };

  const clearCheckpointSelections = () => {
    setSelectedCheckpoints(new Set());
  };

  const selectAllPendingCheckpoints = () => {
    if (!selectedApproval) return;

    const pendingProject = selectedApproval.checkpointApprovals.projectCheckpoints
      .filter(cp => cp.status === 'pending')
      .map(extractCheckpointId)
      .filter(Boolean);

    const pendingAsset = selectedApproval.checkpointApprovals.assetCheckpoints
      .filter(cp => cp.status === 'pending')
      .map(extractCheckpointId)
      .filter(Boolean);

    const allPendingCheckpoints = [...pendingProject, ...pendingAsset];

    if (allPendingCheckpoints.length === 0) {
      toast.info('No pending checkpoints to select');
      return;
    }

    setSelectedCheckpoints(new Set(allPendingCheckpoints));
    toast.success(`Selected ${allPendingCheckpoints.length} pending checkpoint(s)`);
  };

  const handleBulkApproveCheckpoints = async () => {
    if (!selectedAuditId || selectedCheckpoints.size === 0) {
      toast.warning('Please select at least one checkpoint');
      return;
    }

    try {
      setActionLoading(true);
      const checkpointIds = Array.from(selectedCheckpoints);

      await bulkApproveCheckpoints(selectedAuditId, checkpointIds);

      toast.success(`${checkpointIds.length} checkpoint(s) approved successfully`);
      setSelectedCheckpoints(new Set());
      if (selectedAuditId) await refreshAuditApproval(selectedAuditId);
      onApprovalComplete?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to approve checkpoints');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkRejectCheckpoints = () => {
    if (selectedCheckpoints.size === 0) {
      toast.warning('Please select at least one checkpoint');
      return;
    }

    const checkpointIds = Array.from(selectedCheckpoints);
    setBulkRejectCheckpointIds(checkpointIds);
    setRejectActionType('bulk-checkpoints');
    setShowRejectModal(true);
  };

  // An audit has "staged" items when checkpoints were added after the audit was already
  // submitted/approved — those land as 'not_required' until the requester sends them for
  // review. Used to re-show "Send for Review" (incremental re-review of only the new items).
  const auditHasStagedItems = (approval: AmsAuditApproval | undefined): boolean => {
    if (!approval) return false;
    const cps = [
      ...approval.checkpointApprovals.projectCheckpoints,
      ...approval.checkpointApprovals.assetCheckpoints,
    ];
    return cps.some(cp => cp.status === 'not_required');
  };

  // An audit needs (re)submission when it is a fresh draft, or it has newly staged items
  // added after a prior approval (but is not currently rejected — that uses Resubmit).
  const auditNeedsSubmission = (approval: AmsAuditApproval | undefined): boolean => {
    if (!approval) return false;
    const isDraft = !approval.submittedForReview && approval.overallStatus === 'draft';
    const hasStaged = approval.overallStatus !== 'rejected' && auditHasStagedItems(approval);
    return isDraft || hasStaged;
  };

  // Check if all active audits have checkpoints
  const canSubmitAllAudits = (): boolean => {
    return activeAudits.every(audit => {
      const auditId = String(audit._id || (audit as any).id);
      const approval = auditApprovals.get(auditId);
      if (!approval) return false;
      const hasProjectCheckpoints = approval.checkpointApprovals.projectCheckpoints.length > 0;
      const hasAssetCheckpoints = approval.checkpointApprovals.assetCheckpoints.length > 0;
      return hasProjectCheckpoints || hasAssetCheckpoints;
    });
  };

  const handleSubmitForReview = async () => {
    if (!canSubmitAllAudits()) {
      toast.error('All active audits must have checkpoints before submitting');
      return;
    }

    try {
      setActionLoading(true);
      // Submit only the audits that actually need it: fresh drafts, or audits with new
      // items staged after a prior approval. This drives incremental re-review and avoids
      // a redundant "already submitted" error on audits with nothing new.
      const auditsToSubmit = activeAudits.filter(audit => {
        const auditId = String(audit._id || (audit as any).id);
        return auditNeedsSubmission(auditApprovals.get(auditId));
      });

      if (auditsToSubmit.length === 0) {
        toast.info('No audits with new items to submit for review');
        setShowSubmitModal(false);
        return;
      }

      const submitPromises = auditsToSubmit.map(audit => {
        const auditId = String(audit._id || (audit as any).id);
        return submitAuditForReview(auditId, submitComments).catch((error: any) => {
          console.error(`Failed to submit audit ${auditId}:`, error);
          throw error;
        });
      });

      await Promise.all(submitPromises);
      toast.success(`${auditsToSubmit.length} audit(s) submitted for review successfully`);
      setShowSubmitModal(false);
      setSubmitComments('');
      await loadProjectData({ preserveAuditId: selectedAuditId });
      onApprovalComplete?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to submit audits for review');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResubmit = async () => {
    if (!canSubmitAllAudits()) {
      toast.error('All active audits must have checkpoints before resubmitting');
      return;
    }

    try {
      setActionLoading(true);
      // Resubmit all active audits that are rejected
      const rejectedAudits = activeAudits.filter(audit => {
        const auditId = String(audit._id || (audit as any).id);
        const approval = auditApprovals.get(auditId);
        return approval?.overallStatus === 'rejected';
      });

      if (rejectedAudits.length === 0) {
        toast.warning('No rejected audits to resubmit');
        return;
      }

      const resubmitPromises = rejectedAudits.map(audit => {
        const auditId = String(audit._id || (audit as any).id);
        return resubmitAudit(auditId).catch((error: any) => {
          console.error(`Failed to resubmit audit ${auditId}:`, error);
          throw error;
        });
      });
      
      await Promise.all(resubmitPromises);
      toast.success(`${rejectedAudits.length} audit(s) resubmitted successfully`);
      await loadProjectData({ preserveAuditId: selectedAuditId });
      onApprovalComplete?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to resubmit audits');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStartAudit = async () => {
    if (!selectedAuditId) return;
    try {
      setActionLoading(true);
      await startAudit(selectedAuditId);
      toast.success('Audit started successfully');
      await loadProjectData({ preserveAuditId: selectedAuditId });
      onApprovalComplete?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to start audit');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full"><CheckCircle size={12} /> Approved</span>;
      case 'rejected':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full"><XCircle size={12} /> Rejected</span>;
      case 'pending':
      case 'not_required':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full"><Clock size={12} /> Pending</span>;
      default:
        return null;
    }
  };

  // Per-audit approval summary for Audits tab (pending vs done)
  const getAuditApprovalSummary = (approval: AmsAuditApproval | undefined) => {
    if (!approval) return { pending: 0, done: 0, hasPending: false };
    let pending = 0;
    let done = 0;
    // `not_required` items (e.g. the Assets & Equipment section, reviewed via its
    // checkpoints instead) are NOT part of the review — exclude them entirely so a
    // fully-reviewed audit reads 0 pending, matching computeOverallStatus.
    const tally = (status: string) => {
      if (status === 'not_required') return;
      if (status === 'pending') pending++;
      else done++; // approved | rejected
    };
    [approval.sections.auditType, approval.sections.auditors, approval.sections.assetsAndEquipment].forEach(s => tally(s.status));
    [...approval.checkpointApprovals.projectCheckpoints, ...approval.checkpointApprovals.assetCheckpoints].forEach(cp => tally(cp.status));
    return { pending, done, hasPending: pending > 0 };
  };

  // Calculate progress across all active audits
  const calculateProgress = () => {
    let reviewed = 0;
    let total = 0;
    const breakdown: Record<string, { reviewed: number; total: number }> = {};
    let hasRejections = false;

    let sectionsReviewed = 0;
    let sectionsTotal = 0;
    let checkpointsReviewed = 0;
    let checkpointsTotal = 0;

    activeAudits.forEach(audit => {
      const auditId = String(audit._id || (audit as any).id);
      const approval = auditApprovals.get(auditId);
      if (!approval) return;

      // Count only REQUIRED sections — a `not_required` section (e.g. Assets &
      // Equipment, reviewed via checkpoints) is not part of the review, so it must
      // not inflate the denominator (otherwise a fully-approved audit stalls below 100%).
      [approval.sections.auditType, approval.sections.auditors, approval.sections.assetsAndEquipment].forEach(s => {
        if (s.status === 'not_required') return;
        sectionsTotal++;
        if (s.status === 'approved' || s.status === 'rejected') {
          sectionsReviewed++;
          if (s.status === 'rejected') hasRejections = true;
        }
      });

      // Count checkpoints (excluding not_required)
      const allCheckpoints = [
        ...approval.checkpointApprovals.projectCheckpoints,
        ...approval.checkpointApprovals.assetCheckpoints
      ];
      allCheckpoints.forEach(cp => {
        if (cp.status === 'not_required') return;
        checkpointsTotal++;
        if (cp.status === 'approved' || cp.status === 'rejected') {
          checkpointsReviewed++;
          if (cp.status === 'rejected') hasRejections = true;
        }
      });
    });

    breakdown.sections = { reviewed: sectionsReviewed, total: sectionsTotal };
    breakdown.checkpoints = { reviewed: checkpointsReviewed, total: checkpointsTotal };
    reviewed = sectionsReviewed + checkpointsReviewed;
    total = sectionsTotal + checkpointsTotal;

    const percentage = total > 0 ? Math.round((reviewed / total) * 100) : 0;
    return { percentage, reviewed, total, breakdown, hasRejections };
  };

  if (!project) {
    return (
      <RightDrawer open={open} onClose={onClose} title="Project Approval">
        <div className="flex items-center justify-center py-16">
          <div className="w-9 h-9 border-2 border-gray-200 border-t-blue-600 rounded-full animate-spin" />
        </div>
      </RightDrawer>
    );
  }

  const progress = calculateProgress();

  return (
    <>
      <RightDrawer open={open} onClose={onClose} title={`Project Approval - ${project?.projectName || project?.name || 'Unknown'}`} size="full">
        <div className="flex flex-col h-full">
          {/* Progress Bar */}
          <div className="px-5 py-3 bg-white border-b border-gray-200">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex-1 min-w-[180px] flex items-center gap-3">
                <div className="flex-1 max-w-[220px] h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${
                      progress.percentage === 100 ? 'bg-gradient-to-r from-emerald-500 to-emerald-600' : 'bg-gradient-to-r from-blue-500 to-blue-600'
                    }`}
                    style={{ width: `${progress.percentage}%` }}
                  />
                </div>
                <span className="text-sm font-semibold text-gray-700 tabular-nums shrink-0">
                  {progress.reviewed}/{progress.total}
                </span>
                <span className={`text-sm font-bold tabular-nums shrink-0 ${progress.percentage === 100 ? 'text-emerald-600' : 'text-blue-600'}`}>
                  {progress.percentage}%
                </span>
                {progress.breakdown.sections && progress.breakdown.sections.total > 0 && (
                  <span className="text-xs text-gray-400 hidden sm:inline">
                    Sec {progress.breakdown.sections.reviewed}/{progress.breakdown.sections.total}
                  </span>
                )}
                {progress.breakdown.checkpoints && progress.breakdown.checkpoints.total > 0 && (
                  <span className="text-xs text-gray-400 hidden sm:inline">
                    CP {progress.breakdown.checkpoints.reviewed}/{progress.breakdown.checkpoints.total}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="bg-white border-b border-gray-200 px-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex gap-0.5">
                {tabs.map(tab => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-3 py-2 text-xs font-semibold transition-all flex items-center gap-1.5 ${
                        activeTab === tab.id
                          ? 'text-blue-600 border-b-2 border-blue-600'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <Icon className="w-3 h-3" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
              {selectedAuditId && (
                <button
                  type="button"
                  onClick={() => setHistoryOpen(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 my-1 text-xs font-semibold rounded-md border border-gray-300 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors shrink-0"
                >
                  <History className="w-3 h-3" />
                  View history
                </button>
              )}
            </div>
          </div>

          {/* Approver: requester (re)submitted this project for review — show the comment */}
          {userIsApprover && !!project?.approvalSubmittedToManagerComments && (
            <div className="mx-3 mt-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
              <div className="flex items-start gap-2">
                <RefreshCw className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-blue-800">
                    Requester resubmitted this project for review
                    {(project?.resubmissionHistory?.length ?? 0) > 1 ? ` (attempt ${project.resubmissionHistory.length})` : ''}
                  </p>
                  <p className="text-sm text-blue-700 mt-0.5 whitespace-pre-wrap break-words">
                    “{project.approvalSubmittedToManagerComments}”
                  </p>
                  {project?.approvalSubmittedToManagerAt && (
                    <p className="text-xs text-blue-500 mt-1">
                      {new Date(project.approvalSubmittedToManagerAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'projectInfo' && (
              <div className="space-y-6">
                {/* Project-level Overview / Assets & Equipment — read-only. These
                    are informational only: they never require approver action and
                    never block an audit's approval status. See
                    docs/superpowers/specs/2026-07-04-ams-project-level-auto-approve-bulk-checkpoint-design.md */}
                <div className="rounded-lg border border-gray-200 bg-white shadow-sm p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <Building2 className="w-5 h-5 text-gray-600" />
                    <h3 className="text-base font-semibold text-gray-900">Project Info</h3>
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-blue-50 text-blue-700 rounded-full">
                      Informational — no approval required
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Project Overview</p>
                      {projectApprovalSections?.projectOverview?.status && projectApprovalSections.projectOverview.status !== 'not_required' ? (
                        getStatusBadge(projectApprovalSections.projectOverview.status)
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                          <CheckCircle size={12} /> Auto-approved
                        </span>
                      )}
                      {projectApprovalSections?.projectOverview?.approverComments && projectApprovalSections.projectOverview.status === 'rejected' && (
                        <p className="text-xs text-yellow-700 mt-2">{projectApprovalSections.projectOverview.approverComments}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Assets & Equipment</p>
                      {projectApprovalSections?.assetsAndEquipment?.status && projectApprovalSections.assetsAndEquipment.status !== 'not_required' ? (
                        getStatusBadge(projectApprovalSections.assetsAndEquipment.status)
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                          <CheckCircle size={12} /> Auto-approved
                        </span>
                      )}
                      {projectApprovalSections?.assetsAndEquipment?.approverComments && projectApprovalSections.assetsAndEquipment.status === 'rejected' && (
                        <p className="text-xs text-yellow-700 mt-2">{projectApprovalSections.assetsAndEquipment.approverComments}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Header Section - Modernized */}
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg border border-gray-200 shadow-sm p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h2 className="text-xl font-bold text-gray-900">{project.projectName || project.name || 'Unknown Project'}</h2>
                          <p className="text-sm text-gray-500 font-mono mt-0.5">{project.projectId || project.code || 'N/A'}</p>
                        </div>
                      </div>
                      {project.description && (
                        <p className="text-sm text-gray-600 mt-3 leading-relaxed">{project.description}</p>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm ${
                        project.status === 'active' 
                          ? 'bg-green-100 text-green-700 border border-green-200' 
                          : 'bg-gray-100 text-gray-700 border border-gray-200'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${
                          project.status === 'active' ? 'bg-green-500' : 'bg-gray-500'
                        }`} />
                        {project.status ? project.status.charAt(0).toUpperCase() + project.status.slice(1) : 'N/A'}
                      </span>
                      {project.priority && (
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm ${
                          project.priority === 'High' || project.priority === 'Critical'
                            ? 'bg-red-100 text-red-700 border border-red-200'
                            : project.priority === 'Medium'
                            ? 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                            : 'bg-blue-100 text-blue-700 border border-blue-200'
                        }`}>
                          {project.priority}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Basic Info Grid - Modernized */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Hash className="w-4 h-4 text-gray-400" />
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Project Type</p>
                    </div>
                    <p className="text-sm font-semibold text-gray-900">{project.projectType || 'N/A'}</p>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Briefcase className="w-4 h-4 text-gray-400" />
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Category</p>
                    </div>
                    <p className="text-sm font-semibold text-gray-900 capitalize">{project.projectCategory || 'N/A'}</p>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Status</p>
                    </div>
                    <p className="text-sm font-semibold text-gray-900 capitalize">{project.status || 'N/A'}</p>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="w-4 h-4 text-gray-400" />
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Priority</p>
                    </div>
                    <p className="text-sm font-semibold text-gray-900">{project.priority || 'N/A'}</p>
                  </div>
                </div>

                {/* Details Grid - Modernized */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Start Date</p>
                    </div>
                    <p className="text-sm font-semibold text-gray-900">
                      {project.startDate ? format(new Date(project.startDate), 'MMM d, yyyy') : 'Not set'}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">End Date</p>
                    </div>
                    <p className="text-sm font-semibold text-gray-900">
                      {project.endDate ? format(new Date(project.endDate), 'MMM d, yyyy') : 'Not set'}
                    </p>
                  </div>
                  {project.location && (
                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Location</p>
                      </div>
                      <p className="text-sm font-semibold text-gray-900">
                        {typeof project.location === 'object' ? project.location.name : project.location}
                      </p>
                    </div>
                  )}
                </div>

                {/* Key Metrics - Modernized */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {project.capacity && (
                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Zap className="w-4 h-4 text-gray-400" />
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Capacity</p>
                      </div>
                      <p className="text-sm font-semibold text-gray-900">
                        {project.capacity} {project.unit?.toUpperCase() || ''}
                      </p>
                    </div>
                  )}
                  {project.scope && (
                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Target className="w-4 h-4 text-gray-400" />
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Scope</p>
                      </div>
                      <p className="text-sm font-semibold text-gray-900">{project.scope}</p>
                    </div>
                  )}
                  {project.assignedResources?.projectManager && (
                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <User className="w-4 h-4 text-gray-400" />
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Project Manager</p>
                      </div>
                      <p className="text-sm font-semibold text-gray-900">
                        {typeof project.assignedResources.projectManager === 'object' 
                          ? project.assignedResources.projectManager.name 
                          : project.projectManager || 'N/A'}
                      </p>
                    </div>
                  )}
                  {project.assignedResources?.projectHead && (
                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <User className="w-4 h-4 text-gray-400" />
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Project Head</p>
                      </div>
                      <p className="text-sm font-semibold text-gray-900">
                        {typeof project.assignedResources.projectHead === 'object' 
                          ? project.assignedResources.projectHead.name 
                          : 'N/A'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Assigned Resources - Modernized */}
                {project.assignedResources?.auditors && project.assignedResources.auditors.length > 0 && (
                  <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Users className="w-5 h-5 text-gray-600" />
                      <h3 className="text-sm font-semibold text-gray-900">Auditors ({project.assignedResources.auditors.length})</h3>
                    </div>
                    <div className="space-y-2">
                      {project.assignedResources.auditors.map((auditor: any, idx: number) => (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors">
                          <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <User className="w-3 h-3 text-blue-600" />
                          </div>
                          <p className="text-sm font-medium text-gray-900">
                            {typeof auditor === 'object' ? auditor.name : 'N/A'}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">Assets & Equipment</h3>

                <div className="border border-gray-200 rounded-lg p-4">
                  {/* Assets List with Equipment */}
                  {projectAssets.length > 0 ? (
                    <div className="space-y-2 mb-3">
                      {projectAssets.map((asset) => {
                        const assetId = asset._id || asset.id;
                        const isExpanded = expandedAssets.has(assetId);
                        const equipment = getEquipmentByAsset(assetId);
                        
                        return (
                          <div key={assetId} className="border border-gray-200 rounded-md overflow-hidden">
                            <div
                              className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                              onClick={() => toggleAssetExpand(assetId)}
                            >
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                {isExpanded ? (
                                  <ChevronUp size={16} className="text-gray-500 flex-shrink-0" />
                                ) : (
                                  <ChevronDown size={16} className="text-gray-500 flex-shrink-0" />
                                )}
                                <HardDrive size={16} className="text-blue-600 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {asset.name || 'Unknown Asset'}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {equipment.length} equipment{equipment.length !== 1 ? 's' : ''}
                                  </p>
                                </div>
                              </div>
                            </div>
                            
                            {isExpanded && equipment.length > 0 && (
                              <div className="border-t border-gray-200 bg-white">
                                <div className="p-3 space-y-2">
                                  {equipment.map((eq: any) => (
                                    <div key={eq._id || eq.id} className="flex items-center gap-3 p-2 bg-gray-50 rounded-md">
                                      <Package size={14} className="text-gray-400 flex-shrink-0" />
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs font-medium text-gray-900 truncate">
                                          {eq.equipmentName || eq.equipmentId || 'Unknown Equipment'}
                                        </p>
                                        {eq.makeAndModel && (
                                          <p className="text-xs text-gray-500 truncate">
                                            {eq.makeAndModel}
                                          </p>
                                        )}
                                        {eq.serialNumber && (
                                          <p className="text-xs text-gray-500 truncate">
                                            S/N: {eq.serialNumber}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 mb-3">No assets added to this project</p>
                  )}

                  {selectedApproval?.sections.assetsAndEquipment.approverComments && (
                    <div className="p-2 bg-yellow-50 border border-yellow-200 rounded-md mb-3">
                      <p className="text-xs font-medium text-yellow-800 mb-1">Rejection Comments:</p>
                      <p className="text-xs text-yellow-700">{selectedApproval.sections.assetsAndEquipment.approverComments}</p>
                    </div>
                  )}

                  {selectedApproval?.sections.assetsAndEquipment.approverId && (
                    <div className="text-xs text-gray-500 mb-3">
                      {selectedApproval.sections.assetsAndEquipment.status === 'approved' && selectedApproval.sections.assetsAndEquipment.approvedAt && (
                        <span>
                          Approved by {typeof selectedApproval.sections.assetsAndEquipment.approverId === 'object' 
                            ? selectedApproval.sections.assetsAndEquipment.approverId.name 
                            : 'Approver'} on {format(new Date(selectedApproval.sections.assetsAndEquipment.approvedAt), 'MMM d, yyyy HH:mm')}
                        </span>
                      )}
                      {selectedApproval.sections.assetsAndEquipment.status === 'rejected' && selectedApproval.sections.assetsAndEquipment.rejectedAt && (
                        <span>
                          Rejected by {typeof selectedApproval.sections.assetsAndEquipment.approverId === 'object' 
                            ? selectedApproval.sections.assetsAndEquipment.approverId.name 
                            : 'Approver'} on {format(new Date(selectedApproval.sections.assetsAndEquipment.rejectedAt), 'MMM d, yyyy HH:mm')}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'audits' && (
              <div className="rounded-xl border-2 border-gray-200 bg-gray-50/50 shadow-sm overflow-hidden">
                {/* Audit tabs: selected = head of body (no bottom border), unselected = outside boxes */}
                {activeAudits.length > 0 && (
                  <div className="px-4 pt-4 pb-3 flex flex-wrap items-end gap-2">
                    <p className="text-xs font-medium text-gray-500 w-full mb-2">All Audits</p>
                    {activeAudits.map((audit) => {
                      const auditId = String(audit._id || (audit as any).id);
                      const approval = auditApprovals.get(auditId);
                      const summary = getAuditApprovalSummary(approval);
                      const isSelected = selectedAuditId === auditId;
                      let frequencyLabel = '';
                      if (audit.auditFrequency === 'yearly') frequencyLabel = 'Yearly';
                      else if (audit.auditFrequency === 'half_yearly') frequencyLabel = 'Half Yearly';
                      else if (audit.auditFrequency === 'quarterly') frequencyLabel = 'Quarterly';
                      else {
                        const freq = audit.auditFrequency as string | undefined;
                        if (freq && typeof freq === 'string') frequencyLabel = freq.replace('_', ' ');
                      }
                        const pillHighlight = userIsRequester ? getAuditPillHighlightForRequester(approval?.overallStatus) : '';
                        return (
                          <button
                            key={auditId}
                            onClick={() => setSelectedAuditId(auditId)}
                            className={`
                              relative px-4 py-2.5 text-left transition-all duration-200 min-w-[180px]
                              border-2
                              ${isSelected
                                ? 'border-blue-500 bg-blue-50/80 shadow-sm rounded-t-lg border-b-0 rounded-b-none -mb-px z-10 pb-4'
                                : pillHighlight
                                  ? `${pillHighlight} rounded-lg`
                                  : 'border-gray-200 bg-white hover:border-gray-300 rounded-lg'
                              }
                          `}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className={`text-sm font-semibold ${isSelected ? 'text-blue-800' : 'text-gray-900'}`}>
                              {getAuditTypeLabel(audit)} — {frequencyLabel}
                            </span>
                            {summary.hasPending ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 rounded-full">
                                <Clock size={12} /> Pending
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full">
                                <CheckCircle size={12} /> Reviewed
                              </span>
                            )}
                          </div>
                          <div className="mt-1 flex items-center gap-3 text-xs text-gray-500">
                            <span>{summary.pending} pending</span>
                            <span>{summary.done} done</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Body: connects to selected tab; requester: highlight when audit rejected */}
                {selectedAudit && selectedApproval ? (
                  <div className={`p-4 pt-5 space-y-5 rounded-b-xl border-2 border-t-0 -mt-px ${
                    userIsRequester && selectedApproval.overallStatus === 'rejected'
                      ? 'bg-red-50/60 border-red-200 ring-2 ring-red-300/40'
                      : userIsRequester && (selectedApproval.overallStatus === 'approved_to_start' || selectedApproval.overallStatus === 'partially_approved')
                        ? 'bg-green-50/50 border-green-200'
                        : 'bg-blue-50/80 border-gray-200'
                  }`}>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <FileCheck className="w-4 h-4 shrink-0 text-blue-600" />
                      <span className="font-medium text-gray-800">
                        {getAuditTypeLabel(selectedAudit)}
                        {' — '}
                        {selectedAudit.auditFrequency === 'yearly' ? 'Yearly' :
                          selectedAudit.auditFrequency === 'half_yearly' ? 'Half Yearly' :
                          selectedAudit.auditFrequency === 'quarterly' ? 'Quarterly' :
                          (selectedAudit.auditFrequency as string)?.replace('_', ' ') || ''}
                      </span>
                      {getStatusBadge(selectedApproval.overallStatus)}
                    </div>
                    {userIsApprover && !selectedApproval.submittedForReview && (
                      <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700">
                        <AlertCircle className="w-4 h-4 shrink-0" />
                        This audit has not been submitted for review yet — no action can be taken until the requester sends it.
                      </div>
                    )}
                    {/* Audit Info (left) + Auditors (right) — side by side; requester: highlight by status */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* 1) Audit Information — left */}
                      <div className={`rounded-xl border shadow-sm overflow-hidden flex flex-col min-w-0 ${getSectionHighlightForRequester(selectedApproval.sections.auditType.status) || 'border-gray-200 bg-white'}`}>
                        <div className="px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="w-4 h-4 text-blue-600 shrink-0" />
                            <h3 className="text-sm font-semibold text-gray-900 truncate">Audit Information</h3>
                            {getStatusBadge(selectedApproval.sections.auditType.status)}
                          </div>
                          {/* Only an audit the requester has submitted is reviewable. */}
                          {userIsApprover && selectedApproval.submittedForReview && (
                            <div className="flex items-center gap-1.5 shrink-0">
                              {['pending', 'rejected', 'not_required'].includes(selectedApproval.sections.auditType?.status ?? '') && (
                                <button
                                  type="button"
                                  onClick={() => handleApproveSection('auditType')}
                                  disabled={actionLoading}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 text-xs font-medium"
                                >
                                  <CheckCircle size={14} />
                                  Approve
                                </button>
                              )}
                              {['pending', 'approved', 'not_required'].includes(selectedApproval.sections.auditType?.status ?? '') && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setRejectSectionName('auditType');
                                    setRejectActionType('section');
                                    setShowRejectModal(true);
                                  }}
                                  disabled={actionLoading}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border-2 border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50 text-xs font-medium"
                                >
                                  <XCircle size={14} />
                                  Reject
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="p-4 flex-1 min-w-0">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-0.5">Audit Type</p>
                              <p className="text-sm font-semibold text-gray-900">{getAuditTypeLabel(selectedAudit) || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-0.5">Frequency</p>
                              <p className="text-sm font-semibold text-gray-900">
                                {selectedAudit.auditFrequency === 'yearly' ? 'Yearly' :
                                  selectedAudit.auditFrequency === 'half_yearly' ? 'Half Yearly' :
                                  selectedAudit.auditFrequency === 'quarterly' ? 'Quarterly' :
                                  selectedAudit.auditFrequency || 'N/A'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-0.5">Overall Status</p>
                              <div>{getStatusBadge(selectedApproval.overallStatus)}</div>
                            </div>
                            {selectedApproval.submittedAt && (
                              <div>
                                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-0.5">Submitted</p>
                                <p className="text-xs text-gray-900">
                                  {format(new Date(selectedApproval.submittedAt), 'MMM d, yyyy HH:mm')}
                                  {selectedApproval.submittedBy && typeof selectedApproval.submittedBy === 'object' && (
                                    <span className="block text-gray-600 truncate">by {selectedApproval.submittedBy.name}</span>
                                  )}
                                </p>
                              </div>
                            )}
                          </div>
                          {selectedApproval.sections.auditType.approverComments && (
                            <div className="mt-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                              <p className="text-xs font-semibold text-amber-800 mb-0.5">Rejection Comments</p>
                              <p className="text-xs text-amber-700">{selectedApproval.sections.auditType.approverComments}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* 2) Auditors — right */}
                      <div className={`rounded-xl border shadow-sm overflow-hidden flex flex-col min-w-0 ${getSectionHighlightForRequester(selectedApproval.sections.auditors.status) || 'border-gray-200 bg-white'}`}>
                        <div className="px-4 py-3 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <Users className="w-4 h-4 text-slate-600 shrink-0" />
                            <h3 className="text-sm font-semibold text-gray-900">Auditors</h3>
                            {getStatusBadge(selectedApproval.sections.auditors.status)}
                          </div>
                          {/* Only an audit the requester has submitted is reviewable. */}
                          {userIsApprover && selectedApproval.submittedForReview && (
                            <div className="flex items-center gap-1.5 shrink-0">
                              {['pending', 'rejected', 'not_required'].includes(selectedApproval.sections.auditors?.status ?? '') && (
                                <button
                                  type="button"
                                  onClick={() => handleApproveSection('auditors')}
                                  disabled={actionLoading}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 text-xs font-medium"
                                >
                                  <CheckCircle size={14} />
                                  Approve
                                </button>
                              )}
                              {['pending', 'approved', 'not_required'].includes(selectedApproval.sections.auditors?.status ?? '') && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setRejectSectionName('auditors');
                                    setRejectActionType('section');
                                    setShowRejectModal(true);
                                  }}
                                  disabled={actionLoading}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border-2 border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50 text-xs font-medium"
                                >
                                  <XCircle size={14} />
                                  Reject
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="p-4 flex-1 min-w-0 overflow-auto">
                          {selectedAudit.auditors && selectedAudit.auditors.length > 0 ? (
                            <div className="space-y-1.5">
                              {selectedAudit.auditors.map((auditor: any) => (
                                <div key={auditor._id || auditor.id} className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg border border-gray-100">
                                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                    <User size={14} className="text-blue-600" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">{auditor.name || 'Unknown'}</p>
                                    <p className="text-xs text-gray-500 truncate">{auditor.email || 'No email'}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500">No auditors assigned</p>
                          )}
                          {selectedApproval.sections.auditors.approverComments && (
                            <div className="mt-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                              <p className="text-xs font-semibold text-amber-800 mb-0.5">Rejection Comments</p>
                              <p className="text-xs text-amber-700">{selectedApproval.sections.auditors.approverComments}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* 3) Assets-wise checkpoints — grouped by asset, each checkpoint uses CheckpointDetailCard */}
                    <div className="space-y-6">
                      {/* Bulk checkpoint approve/reject — only when there's something pending to act on */}
                      {userIsApprover && selectedApproval.submittedForReview && (() => {
                        const totalPending = [
                          ...selectedApproval.checkpointApprovals.projectCheckpoints,
                          ...selectedApproval.checkpointApprovals.assetCheckpoints,
                        ].filter((cp) => cp.status === 'pending').length;
                        if (totalPending === 0) return null;
                        return (
                          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
                            <div className="flex flex-wrap items-center gap-3">
                              <ListChecks className="w-4 h-4 text-blue-600" />
                              {selectedCheckpoints.size > 0 ? (
                                <span className="text-sm font-semibold text-blue-900">
                                  {selectedCheckpoints.size} checkpoint{selectedCheckpoints.size !== 1 ? 's' : ''} selected
                                </span>
                              ) : (
                                <span className="text-sm text-blue-800">
                                  {totalPending} checkpoint{totalPending !== 1 ? 's' : ''} pending review
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={selectAllPendingCheckpoints}
                                className="text-sm font-medium text-blue-700 hover:text-blue-900 underline underline-offset-2"
                              >
                                Select all pending
                              </button>
                              {selectedCheckpoints.size > 0 && (
                                <button
                                  type="button"
                                  onClick={clearCheckpointSelections}
                                  className="text-sm font-medium text-gray-600 hover:text-gray-800 underline underline-offset-2"
                                >
                                  Clear
                                </button>
                              )}
                            </div>
                            {selectedCheckpoints.size > 0 && (
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={handleBulkApproveCheckpoints}
                                  disabled={actionLoading}
                                  aria-label={`Approve ${selectedCheckpoints.size} selected checkpoints`}
                                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors text-sm font-medium shadow-sm hover:shadow"
                                >
                                  <CheckCircle size={16} />
                                  Approve Selected ({selectedCheckpoints.size})
                                </button>
                                <button
                                  type="button"
                                  onClick={handleBulkRejectCheckpoints}
                                  disabled={actionLoading}
                                  aria-label={`Reject ${selectedCheckpoints.size} selected checkpoints`}
                                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md border-2 border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50 transition-colors text-sm font-medium"
                                >
                                  <XCircle size={16} />
                                  Reject Selected ({selectedCheckpoints.size})
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Project-level checkpoints */}
                      {selectedApproval.checkpointApprovals.projectCheckpoints.length > 0 && (
                        <div className="space-y-4">
                          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                            <ListChecks className="w-4 h-4 text-blue-600" />
                            Project Checkpoints ({selectedApproval.checkpointApprovals.projectCheckpoints.length})
                          </h3>
                          <div className="space-y-3">
                            {selectedApproval.checkpointApprovals.projectCheckpoints.map((cp, cpIdx) => {
                              let checkpointId: string;
                              if (typeof cp.checkpointId === 'object' && cp.checkpointId) {
                                checkpointId = cp.checkpointId._id || (cp.checkpointId as any).id || String(cp.checkpointId);
                              } else {
                                checkpointId = String(cp.checkpointId || '');
                              }
                              // Never silently drop an unresolved ref (see asset map above) — a
                              // removed checkpoint would stay counted as pending and strand the audit.
                              const isUnresolved = !checkpointId;
                              const checkpointName = typeof cp.checkpointId === 'object' && cp.checkpointId?.name
                                ? cp.checkpointId.name
                                : isUnresolved
                                  ? 'Checkpoint unavailable (removed)'
                                  : 'Unknown Checkpoint';
                              const detailedCheckpoint = getCheckpointDetails(checkpointId, 'project');
                              const canAct = userIsApprover && selectedApproval.submittedForReview && !isUnresolved;
                              return (
                                <CheckpointDetailCard
                                  key={isUnresolved ? `unresolved-project-${cpIdx}` : checkpointId}
                                  checkpoint={detailedCheckpoint}
                                  approvalStatus={{
                                    status: cp.status,
                                    approverComments: cp.approverComments,
                                    approverId: cp.approverId,
                                    approvedAt: cp.approvedAt,
                                    rejectedAt: cp.rejectedAt,
                                  }}
                                  checkpointName={checkpointName}
                                  checkpointId={checkpointId}
                                  onApprove={canAct ? handleApproveCheckpoint : undefined}
                                  onReject={
                                    canAct
                                      ? (id: string) => {
                                          setRejectCheckpointId(id);
                                          setRejectActionType('checkpoint');
                                          setShowRejectModal(true);
                                        }
                                      : undefined
                                  }
                                  showActions={canAct}
                                  actionLoading={actionLoading}
                                  type="project"
                                  selected={selectedCheckpoints.has(checkpointId)}
                                  onToggleSelect={canAct ? toggleCheckpointSelection : undefined}
                                />
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Asset-wise checkpoints — grouped by asset, CheckpointDetailCard per checkpoint */}
                      {selectedApproval.checkpointApprovals.assetCheckpoints.length > 0 && (
                        <div className="space-y-4">
                          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                            <HardDrive className="w-4 h-4 text-emerald-600" />
                            Asset Checkpoints ({selectedApproval.checkpointApprovals.assetCheckpoints.length})
                          </h3>
                          {(() => {
                            const groupedByAsset = selectedApproval.checkpointApprovals.assetCheckpoints.reduce((acc: Record<string, { assetName: string; checkpoints: typeof selectedApproval.checkpointApprovals.assetCheckpoints }>, cp) => {
                              const raw = cp.amsProjectAssetId as any;
                              // AMS project assets expose the `id` virtual, not `_id` (toJSON quirk),
                              // so key on `_id || id` — else every asset collapses into one group.
                              const assetId = typeof raw === 'object' && raw
                                ? (raw._id || raw.id || 'unknown')
                                : typeof raw === 'string' ? raw : 'unknown';
                              const assetName = typeof raw === 'object' && raw?.name
                                ? raw.name
                                : 'Unknown Asset';
                              if (!acc[assetId]) acc[assetId] = { assetName, checkpoints: [] };
                              acc[assetId].checkpoints.push(cp);
                              return acc;
                            }, {});
                            return Object.entries(groupedByAsset).map(([assetId, data]) => (
                              <div key={assetId} className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                                <div className="px-4 py-3 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-gray-200 flex items-center gap-2">
                                  <HardDrive size={18} className="text-emerald-600" />
                                  <span className="text-sm font-semibold text-gray-900">{data.assetName}</span>
                                  <span className="text-xs text-gray-500">({data.checkpoints.length} checkpoint{data.checkpoints.length !== 1 ? 's' : ''})</span>
                                </div>
                                <div className="space-y-3 p-4">
                                  {data.checkpoints.map((cp, cpIdx) => {
                                    let checkpointId: string;
                                    if (typeof cp.checkpointId === 'object' && cp.checkpointId) {
                                      checkpointId = cp.checkpointId._id || (cp.checkpointId as any).id || String(cp.checkpointId);
                                    } else {
                                      checkpointId = String(cp.checkpointId || '');
                                    }
                                    // A ref that failed to resolve (checkpoint removed) must NOT be
                                    // silently dropped — it stays counted as pending and would strand
                                    // the audit invisibly. Surface it as a labelled, non-actionable card.
                                    const isUnresolved = !checkpointId;
                                    const checkpointName = typeof cp.checkpointId === 'object' && cp.checkpointId?.name
                                      ? cp.checkpointId.name
                                      : isUnresolved
                                        ? 'Checkpoint unavailable (removed)'
                                        : 'Unknown Checkpoint';
                                    const detailedCheckpoint = getCheckpointDetails(checkpointId, 'asset');
                                    const canAct = userIsApprover && !isUnresolved;
                                    return (
                                      <CheckpointDetailCard
                                        key={isUnresolved ? `unresolved-asset-${data.assetName}-${cpIdx}` : checkpointId}
                                        checkpoint={detailedCheckpoint}
                                        approvalStatus={{
                                          status: cp.status,
                                          approverComments: cp.approverComments,
                                          approverId: cp.approverId,
                                          approvedAt: cp.approvedAt,
                                          rejectedAt: cp.rejectedAt,
                                        }}
                                        checkpointName={checkpointName}
                                        checkpointId={checkpointId}
                                        onApprove={canAct ? handleApproveCheckpoint : undefined}
                                        onReject={
                                          canAct
                                            ? (id: string) => {
                                                setRejectCheckpointId(id);
                                                setRejectActionType('checkpoint');
                                                setShowRejectModal(true);
                                              }
                                            : undefined
                                        }
                                        showActions={canAct}
                                        actionLoading={actionLoading}
                                        type="asset"
                                        assetName={data.assetName}
                                        selected={selectedCheckpoints.has(checkpointId)}
                                        onToggleSelect={canAct ? toggleCheckpointSelection : undefined}
                                      />
                                    );
                                  })}
                                </div>
                              </div>
                            ));
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="p-6 text-center text-sm text-gray-500 border-t border-gray-100">
                    {activeAudits.length === 0 ? 'No active audits found' : 'Select an audit above to view details and approval actions'}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons at Bottom */}
          <div className="flex gap-2 pt-4 border-t px-6 pb-4">
            {userIsRequester && (() => {
              // Show "Send for Review" when any active audit is a fresh draft OR has new
              // items staged after a prior approval (incremental re-review of new items).
              const hasSubmittableAudits = activeAudits.some(audit => {
                const auditId = String(audit._id || (audit as any).id);
                return auditNeedsSubmission(auditApprovals.get(auditId));
              });

              if (hasSubmittableAudits) {
                const canSubmit = canSubmitAllAudits();
                return (
                  <button
                    type="button"
                    onClick={() => setShowSubmitModal(true)}
                    disabled={actionLoading || !canSubmit}
                    title={!canSubmit ? 'All active audits must have checkpoints before submitting' : ''}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  >
                    {actionLoading && (
                      <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    )}
                    Send for Review
                  </button>
                );
              }
              return null;
            })()}
            {userIsRequester && (() => {
              // Check if any active audit is rejected
              const hasRejectedAudits = activeAudits.some(audit => {
                const auditId = String(audit._id || (audit as any).id);
                const approval = auditApprovals.get(auditId);
                return approval && approval.overallStatus === 'rejected';
              });
              
              if (hasRejectedAudits) {
                const canResubmit = canSubmitAllAudits();
                return (
                  <button
                    type="button"
                    onClick={handleResubmit}
                    disabled={actionLoading || !canResubmit}
                    title={!canResubmit ? 'All active audits must have checkpoints before resubmitting' : ''}
                    className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 border border-transparent rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  >
                    {actionLoading && (
                      <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    )}
                    Resubmit
                  </button>
                );
              }
              return null;
            })()}
            {selectedApproval && selectedApproval.overallStatus === 'approved_to_start' && !selectedApproval.auditStarted && !auditHasStagedItems(selectedApproval) && (
              <button
                type="button"
                onClick={handleStartAudit}
                disabled={actionLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {actionLoading && (
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                Start Audit
              </button>
            )}
          </div>
        </div>
      </RightDrawer>

      {/* Reject Modal */}
      <RejectModal
        open={showRejectModal}
        onClose={() => {
          console.log('RejectModal onClose called');
          setShowRejectModal(false);
          setRejectSectionName(null);
          setRejectCheckpointId(null);
          setRejectComments('');
          setRejectActionType(null);
          setBulkRejectCheckpointIds([]);
        }}
        onConfirm={() => {
          if (rejectActionType === 'section') {
            handleRejectSection();
          } else {
            handleRejectCheckpoint();
          }
        }}
        title={
          rejectActionType === 'bulk-checkpoints'
            ? `Reject ${bulkRejectCheckpointIds.length} Checkpoint(s)`
            : rejectSectionName
              ? `Reject ${rejectSectionName}`
              : 'Reject Checkpoint'
        }
        message={
          rejectActionType === 'bulk-checkpoints'
            ? `You are about to reject ${bulkRejectCheckpointIds.length} checkpoint(s). Please provide a reason for rejection.`
            : 'Please provide a reason for rejection. This comment will be visible to the requester.'
        }
        comments={rejectComments}
        onCommentsChange={setRejectComments}
        loading={actionLoading}
      />

      {/* Submit Modal */}
      <Dialog
        visible={showSubmitModal}
        onHide={() => {
          setShowSubmitModal(false);
          setSubmitComments('');
        }}
        header="Submit Audit for Review"
        modal
        blockScroll
        dismissableMask
        style={{ width: '500px' }}
        footer={
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                setShowSubmitModal(false);
                setSubmitComments('');
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmitForReview}
              disabled={actionLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {actionLoading && (
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              Submit
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <label className="block text-sm font-medium text-gray-700">Comments (Optional)</label>
          <textarea
            value={submitComments}
            onChange={(e) => setSubmitComments(e.target.value)}
            rows={5}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            placeholder="Add any comments for the approver..."
          />
        </div>
      </Dialog>

      {/* Audit History Drawer — for the currently-selected audit */}
      {selectedAuditId && (
        <AmsAuditHistoryDrawer
          auditId={selectedAuditId}
          isOpen={historyOpen}
          onClose={() => setHistoryOpen(false)}
        />
      )}

    </>
  );
};

export default AmsAuditApprovalDrawer;
